import React, { useRef } from "react";
import {
  Plus,
  Trash2,
  Upload,
  Loader2,
  CheckCircle2,
  Image,
  Video,
  MessageSquare,
  ExternalLink,
  Phone,
} from "lucide-react";

import { Card, Label, Hint, TextInput, TextArea, Segmented } from "./ui";
import { countVariables } from "../templateUtils";

const MEDIA_TYPES = [
  { value: "IMAGE", label: "Image cards", icon: Image },
  { value: "VIDEO", label: "Video cards", icon: Video },
];

const BTN_TYPES = {
  QUICK_REPLY: { label: "Quick reply", icon: MessageSquare },
  URL: { label: "Website", icon: ExternalLink },
  PHONE_NUMBER: { label: "Call", icon: Phone },
};

let cardId = 1;

const blankCard = (format) => ({
  id: `c${cardId++}`,
  header: { format, media_handle: "", media_name: "", media_preview: "" },
  body: { text: "", examples: [] },
  buttons: [],
});

const CardMedia = ({ card, format, onPatch, onUpload, uploading }) => {
  const ref = useRef(null);
  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    onPatch({ media_name: file.name, media_preview: URL.createObjectURL(file) });
    onUpload(file, (handle) => onPatch({ media_handle: handle || "" }));
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] font-medium text-slate-500 transition hover:border-[#37a1d2] hover:bg-[#eaf4fa]/50"
      >
        {uploading ? (
          <Loader2 size={14} className="animate-spin text-[#147db5]" />
        ) : card.header.media_handle ? (
          <CheckCircle2 size={14} className="text-emerald-500" />
        ) : (
          <Upload size={14} />
        )}
        {card.header.media_name || `Upload ${format.toLowerCase()}`}
      </button>
      <input
        ref={ref}
        type="file"
        className="hidden"
        accept={format === "IMAGE" ? "image/jpeg,image/png" : "video/mp4"}
        onChange={pick}
      />
    </div>
  );
};

const CarouselCard = ({ carousel, onChange, onUpload, uploading }) => {
  const cards = carousel.cards;
  const mediaType = cards[0]?.header.format || "IMAGE";

  const setCards = (next) => onChange({ ...carousel, cards: next });

  const setMediaType = (format) =>
    setCards(
      cards.length
        ? cards.map((c) => ({ ...c, header: { ...c.header, format } }))
        : [blankCard(format), blankCard(format)],
    );

  const addCard = () => {
    if (cards.length >= 10) return;
    setCards([...cards, blankCard(mediaType)]);
  };

  const removeCard = (id) => setCards(cards.filter((c) => c.id !== id));

  const patchCardHeader = (id, patch) =>
    setCards(
      cards.map((c) =>
        c.id === id ? { ...c, header: { ...c.header, ...patch } } : c,
      ),
    );

  const patchCardBody = (id, text) => {
    const n = countVariables(text);
    setCards(
      cards.map((c) => {
        if (c.id !== id) return c;
        const examples = [...c.body.examples];
        examples.length = n;
        return { ...c, body: { text, examples: Array.from(examples, (v) => v || "") } };
      }),
    );
  };

  const setCardExample = (id, i, value) =>
    setCards(
      cards.map((c) => {
        if (c.id !== id) return c;
        const examples = [...c.body.examples];
        examples[i] = value;
        return { ...c, body: { ...c.body, examples } };
      }),
    );

  const addButton = (id, type) =>
    setCards(
      cards.map((c) => {
        if (c.id !== id || c.buttons.length >= 2) return c;
        return {
          ...c,
          buttons: [
            ...c.buttons,
            { id: `cb${cardId++}`, type, text: "", url: "", phone_number: "" },
          ],
        };
      }),
    );

  const patchButton = (cardIdV, btnId, patch) =>
    setCards(
      cards.map((c) =>
        c.id === cardIdV
          ? {
              ...c,
              buttons: c.buttons.map((b) =>
                b.id === btnId ? { ...b, ...patch } : b,
              ),
            }
          : c,
      ),
    );

  const removeButton = (cardIdV, btnId) =>
    setCards(
      cards.map((c) =>
        c.id === cardIdV
          ? { ...c, buttons: c.buttons.filter((b) => b.id !== btnId) }
          : c,
      ),
    );

  return (
    <Card className="space-y-4">
      <div>
        <Label>Card media</Label>
        <Segmented options={MEDIA_TYPES} value={mediaType} onChange={setMediaType} />
        <Hint>All cards share the same media type and button layout.</Hint>
      </div>

      {cards.map((card, index) => (
        <div
          key={card.id}
          className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5"
        >
          <div className="mb-2.5 flex items-center justify-between">
            <span className="text-[12px] font-semibold text-slate-600">
              Card {index + 1}
            </span>
            {cards.length > 2 && (
              <button
                type="button"
                onClick={() => removeCard(card.id)}
                className="rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>

          <div className="space-y-2.5">
            <CardMedia
              card={card}
              format={mediaType}
              onPatch={(patch) => patchCardHeader(card.id, patch)}
              onUpload={onUpload}
              uploading={uploading}
            />

            <TextArea
              rows={3}
              value={card.body.text}
              maxLength={160}
              onChange={(e) => patchCardBody(card.id, e.target.value)}
              placeholder="Card message — up to 160 characters"
            />

            {countVariables(card.body.text) > 0 && (
              <div className="space-y-1.5">
                {Array.from({ length: countVariables(card.body.text) }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-9 rounded bg-[#d4e9f5] py-1 text-center text-[10px] font-semibold text-[#0f6392]">
                      {`{{${i + 1}}}`}
                    </span>
                    <TextInput
                      value={card.body.examples[i] || ""}
                      onChange={(e) => setCardExample(card.id, i, e.target.value)}
                      placeholder={`Sample for {{${i + 1}}}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {card.buttons.map((b) => (
              <div key={b.id} className="flex items-center gap-2">
                <TextInput
                  value={b.text}
                  maxLength={25}
                  onChange={(e) => patchButton(card.id, b.id, { text: e.target.value })}
                  placeholder={`${BTN_TYPES[b.type].label} label`}
                />
                {b.type === "URL" && (
                  <TextInput
                    value={b.url}
                    onChange={(e) => patchButton(card.id, b.id, { url: e.target.value })}
                    placeholder="https://…"
                  />
                )}
                {b.type === "PHONE_NUMBER" && (
                  <TextInput
                    value={b.phone_number}
                    onChange={(e) =>
                      patchButton(card.id, b.id, { phone_number: e.target.value })
                    }
                    placeholder="+15551234567"
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeButton(card.id, b.id)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            {card.buttons.length < 2 && (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(BTN_TYPES).map(([type, { label, icon: Icon }]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addButton(card.id, type)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-500 transition hover:border-[#7cc4e8] hover:text-[#147db5]"
                  >
                    <Plus size={10} />
                    <Icon size={11} />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addCard}
        disabled={cards.length >= 10}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-2.5 text-[12px] font-medium text-slate-500 transition hover:border-[#37a1d2] hover:text-[#147db5] disabled:opacity-40"
      >
        <Plus size={14} />
        Add card ({cards.length}/10)
      </button>
    </Card>
  );
};

export default CarouselCard;
